import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "../../../components/base/Modal";
import { Badge, Button, EmptyState, Input, Table, notifyError, type TableColumn } from "../../../components/ui";
import { SectionFrame } from "../components/SectionFrame";
import { useCounterparties, useRadarMutation } from "../hooks/useRadarCorreo";
import { radarCorreoApi, apiErrorMessage, type Counterparty, type CounterpartyRole } from "../services/radarCorreoService";
import { fmtDate } from "../lib/format";

const ROLES: CounterpartyRole[] = ["carrier", "forwarder", "customs_broker", "supplier", "customer", "other"];

function EditCounterparty({ cp, onClose }: { cp: Counterparty; onClose: () => void }) {
  const { t } = useTranslation("radarCorreo");
  const [name, setName] = useState(cp.displayName);
  const [roles, setRoles] = useState<CounterpartyRole[]>(cp.roles);
  const save = useRadarMutation(() => radarCorreoApi.updateCounterparty(cp.id, { displayName: name, roles }));
  const content = (
    <form
      className="space-y-4 p-4 sm:p-6"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(undefined, { onSuccess: onClose, onError: (err) => void notifyError(t("common.error"), apiErrorMessage(err) || undefined) });
      }}
    >
      <Input label={t("counterparties.name")} value={name} onChange={(e) => setName(e.target.value)} required />
      <fieldset>
        <legend className="mb-1 text-sm text-content">{t("counterparties.rolesLabel")}</legend>
        <div className="flex flex-wrap gap-3">
          {ROLES.map((r) => (
            <label key={r} className="flex items-center gap-1.5 text-sm text-content">
              <input type="checkbox" checked={roles.includes(r)} onChange={(e) => setRoles(e.target.checked ? [...roles, r] : roles.filter((x) => x !== r))} />
              {t(`counterparties.roles.${r}`)}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" type="button" onClick={onClose}>{t("common.cancel")}</Button>
        <Button type="submit" loading={save.isPending} disabled={!roles.length || !name.trim()}>{t("common.save")}</Button>
      </div>
    </form>
  );
  return <Modal title={t("counterparties.editTitle")} onClose={onClose} content={content} width="max-w-md w-full" disableCRUD />;
}

export default function ContrapartesSection() {
  const { t, i18n } = useTranslation("radarCorreo");
  const cps = useCounterparties();
  const [editing, setEditing] = useState<Counterparty | null>(null);

  const columns = useMemo<TableColumn<Counterparty>[]>(
    () => [
      { key: "name", header: t("counterparties.columns.name"), render: (c) => <span className="font-medium text-content">{c.displayName}</span> },
      { key: "domain", header: t("counterparties.columns.domain"), hideOnMobile: true, render: (c) => c.key },
      {
        key: "roles",
        header: t("counterparties.columns.roles"),
        render: (c) => (
          <div className="flex flex-wrap gap-1">
            {c.roles.map((r) => (
              <Badge key={r} tone={r === "carrier" || r === "forwarder" ? "primary" : "neutral"} size="sm">
                {t(`counterparties.roles.${r}`)}
              </Badge>
            ))}
            {c.roleSource === "user" && <Badge tone="neutral" variant="outline" size="sm">{t("counterparties.manual")}</Badge>}
          </div>
        ),
      },
      { key: "last", header: t("counterparties.columns.lastSeen"), hideOnMobile: true, render: (c) => fmtDate(c.lastSeen, i18n.language) },
      {
        key: "edit",
        header: "",
        align: "right",
        render: (c) => (
          <Button size="xs" variant="ghost" icon={<i className="ri-edit-line" />} onClick={() => setEditing(c)}>
            {t("counterparties.edit")}
          </Button>
        ),
      },
    ],
    [t, i18n.language],
  );

  return (
    <SectionFrame title={t("counterparties.title")} subtitle={t("counterparties.subtitle")} loading={cps.isLoading} error={cps.error} onRetry={() => void cps.refetch()}>
      {(cps.data || []).length === 0 ? (
        <EmptyState icon="ri-building-line" title={t("counterparties.empty")} />
      ) : (
        <Table columns={columns} rows={cps.data || []} rowKey={(c) => c.id} mobileCards />
      )}
      {editing && <EditCounterparty cp={editing} onClose={() => setEditing(null)} />}
    </SectionFrame>
  );
}
